import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import DocumentIcon from "assets/icons/DocumentIcon";
import RegistrationDocumentDeadlineDialog from "./RegistrationDocumentDeadlineDialog";
import GradeAdvancementDialog from "./GradeAdvancementDialog";
import GraduationMonthDialog from "./GraduationMonthDialog";
import { CLASS_PATH } from "router/pathName";
import { NEW, STUDENT, TEACHER } from "config/keywords";
import useAppSelector from "hooks/useAppSelector";

// Small sakura icon component using the existing sakura.svg
const SakuraIcon = ({ width = 16, height = 16, className = "" }) => {
  return (
    <img
      src="/images/sakura.svg"
      alt="sakura"
      width={width}
      height={height}
      className={className}
    />
  );
};

interface SmallOperationButtonProps {
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  variant?: "green" | "light-red";
  icon?: React.ReactNode;
  extraPadding?: boolean;
  className?: string;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  style?: React.CSSProperties;
}

const SmallOperationButton: React.FC<SmallOperationButtonProps> = ({
  label,
  disabled = false,
  onClick,
  variant = "green",
  icon,
  extraPadding = false,
  className = "",
  buttonRef,
  style,
}) => {
  const getButtonStyles = () => {
    if (disabled) {
      return "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed";
    }

    switch (variant) {
      case "light-red":
        return "bg-red-50 text-red-600 border-red-300 hover:bg-red-100 hover:text-red-700 transition-colors";
      case "green":
      default:
        return "bg-white text-primary border-primary hover:bg-primary hover:text-white transition-colors";
    }
  };

  const paddingClass = extraPadding ? "px-3 py-1" : "px-2 py-1";

  return (
    <button
      type="button"
      className={`${paddingClass} border rounded text-xs flex items-center justify-center whitespace-nowrap gap-1 ${getButtonStyles()} ${className}`}
      onClick={onClick}
      disabled={disabled}
      ref={buttonRef}
      style={style}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {label}
    </button>
  );
};

interface AddStudentButtonProps {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
  style?: React.CSSProperties;
}

const AddStudentButton = ({
  label,
  disabled = false,
  onClick,
  buttonRef,
  style,
}: AddStudentButtonProps) => {
  return (
    <button
      type="button"
      ref={buttonRef}
      className={`py-2 border rounded flex items-center justify-center text-xs transition-colors duration-200 whitespace-nowrap px-3 ${
        disabled
          ? "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
          : "border-primary bg-white text-primary hover:bg-primary hover:text-white"
      }`}
      onClick={onClick}
      disabled={disabled}
      style={style}
    >
      {label}
    </button>
  );
};

const OperationButtons = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAppSelector((state) => state.user);
  const [selectedOperation, setSelectedOperation] = useState("");
  const [buttonWidth, setButtonWidth] = useState<number>();
  const isTeacher = user?.role === TEACHER;

  const addButtonRef = useRef<HTMLButtonElement>(null);
  const registrationButtonRef = useRef<HTMLButtonElement>(null);
  const advancementButtonRef = useRef<HTMLButtonElement>(null);
  const graduationButtonRef = useRef<HTMLButtonElement>(null);

  const addStudentLabel = t("_student._add_student");
  const registrationLabel = t("_student._issue_registration_form");
  const advancementLabel = t("_student._grade_advancement");
  const graduationLabel = t("_student._set_graduation_date");

  useEffect(() => {
    const widths = [
      addButtonRef.current?.offsetWidth ?? 0,
      registrationButtonRef.current?.offsetWidth ?? 0,
      advancementButtonRef.current?.offsetWidth ?? 0,
      graduationButtonRef.current?.offsetWidth ?? 0,
    ].filter((width) => width > 0);

    if (widths.length) {
      setButtonWidth(Math.max(...widths));
    }
  }, [addStudentLabel, registrationLabel, advancementLabel, graduationLabel]);

  const widthStyle = buttonWidth ? { width: buttonWidth } : undefined;

  const handleOperationClick = (operation: string) => {
    setSelectedOperation(operation);
  };

  const renderDialog = () => {
    switch (selectedOperation) {
      case "registration":
        return (
          <RegistrationDocumentDeadlineDialog
            onNoClick={() => setSelectedOperation("")}
            onYesClick={() => setSelectedOperation("")}
          />
        );
      case "advancement":
        return (
          <GradeAdvancementDialog
            onNoClick={() => setSelectedOperation("")}
            onYesClick={() => setSelectedOperation("")}
          />
        );
      case "graduation":
        return (
          <GraduationMonthDialog
            onNoClick={() => setSelectedOperation("")}
            onYesClick={() => setSelectedOperation("")}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="w-full flex flex-wrap gap-2 md:flex-nowrap md:overflow-x-auto">
        <AddStudentButton
          label={addStudentLabel}
          disabled={isTeacher}
          onClick={() => navigate(`${CLASS_PATH}/${id}/${STUDENT}/${NEW}`)}
          buttonRef={addButtonRef}
          style={widthStyle}
        />
        <SmallOperationButton
          label={registrationLabel}
          onClick={() => handleOperationClick("registration")}
          variant="green"
          icon={<DocumentIcon width={16} height={16} color="currentColor" />}
          buttonRef={registrationButtonRef}
          style={widthStyle}
        />
        <SmallOperationButton
          label={advancementLabel}
          onClick={() => handleOperationClick("advancement")}
          variant="light-red"
          icon={<SakuraIcon width={16} height={16} />}
          extraPadding={true}
          buttonRef={advancementButtonRef}
          style={widthStyle}
        />
        <SmallOperationButton
          label={graduationLabel}
          onClick={() => handleOperationClick("graduation")}
          variant="light-red"
          icon={<SakuraIcon width={16} height={16} />}
          extraPadding={true}
          buttonRef={graduationButtonRef}
          style={widthStyle}
        />
      </div>
      {renderDialog()}
    </>
  );
};

export default OperationButtons;
